import { Avatar, Box, Chip, Tooltip } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import EditSelectCell from 'components/NCO/EditSelectCell'
import dayjs from 'dayjs'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { User } from 'types'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import EditLeadScoreCell, { daOptions, industryOptions } from '../../Leads/EditLeadScoreCell'
import EditUserSelectCell from '../../Leads/EditUserSelectCell'

const statusOptions = [
  { value: 'Not Contacted', label: 'Not Contacted' },
  { value: 'Contacted', label: 'Contacted' },
]

const qualityOptions = [
  { value: 'Pending Review', label: 'Pending Review' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Not Qualified', label: 'Not Qualified' },
]

const StatusCell = ({ row, onClick }) => {
  const status = row.status || 'Not Contacted'

  const getColor = value => {
    switch (value) {
      case 'Contacted':
        return 'success'
      default:
        return 'primary'
    }
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
      }}
    >
      <Chip label={status} size="small" color={getColor(status)} sx={{ width: '110px' }} />
    </Box>
  )
}

const QualityCell = ({ row, onClick, densityStyles }) => {
  const quality = row.quality || 'Pending Review'

  const getColor = value => {
    switch (value) {
      case 'Qualified':
        return 'success'
      case 'Not Qualified':
        return 'error'
      default:
        return 'warning'
    }
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
      }}
    >
      <Chip label={quality} size="small" color={getColor(quality)} sx={{ width: '120px' }} />
    </Box>
  )
}

const getScoreColor = (score: number | undefined) => {
  if (score === undefined) return 'grey.500'
  if (score <= 1) return 'error.main'
  if (score === 2) return 'warning.main'
  if (score === 3) return 'info.main'
  if (score === 4) return 'success.light'
  return 'success.main'
}

const LeadScoreCell = ({ row, onClick, densityStyles }) => {
  const score = row.leadScore
  const da = row.leadScoreDA
  const industry = row.leadScoreIndustry

  const daLabel = da ? daOptions.find(o => o.value === da)?.label : null
  const industryLabel = industry ? industryOptions.find(o => o.value === industry)?.label : null

  const tooltipContent = daLabel && industryLabel ? `${daLabel}\n${industryLabel}` : 'Click to set score'

  return (
    <Tooltip title={tooltipContent} placement="top">
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            height: densityStyles.avatarSize,
            width: densityStyles.avatarSize,
            borderRadius: '50%',
            bgcolor: getScoreColor(score),
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: densityStyles.fontSize,
          }}
        >
          {score ?? '-'}
        </Box>
      </Box>
    </Tooltip>
  )
}

const AssignedUserCell = ({ row, densityStyles, users, onClick }) => {
  const assignedUser = row.assignedUser || null
  const selectedUser = users?.find(u => u.user_uuid === assignedUser)

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        padding: densityStyles?.padding,
        gap: densityStyles?.gap,
        cursor: 'pointer',
      }}
    >
      {selectedUser ? (
        <EntityAvatar photoUrl={selectedUser.image_512} size={densityStyles.size} />
      ) : (
        <Box
          sx={{
            width: densityStyles?.avatarSize,
            height: densityStyles?.avatarSize,
            borderRadius: '50%',
            backgroundColor: 'background.defaultdim',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: densityStyles?.fontSize,
          }}
        >
          <Avatar sx={{ opacity: 0.1 }} />
        </Box>
      )}
    </Box>
  )
}

export const renderLeadsColumns = (density = 'standard', onLeadClick, users: User[] = []) => {
  const densityStyles = getDensityStyles(density)

  return [
    {
      field: 'name',
      headerName: 'Name',
      width: 100,
      resizable: true,
      valueGetter: params => params,
      renderCell: params => {
        return (
          <Box component="span" onClick={() => onLeadClick(params.row)} sx={{ cursor: 'pointer' }}>
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      resizable: true,
      editable: true,
      valueGetter: (_, row) => row?.status || 'Not Contacted',
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return <StatusCell row={params.row} onClick={handleClick} />
      },
      renderEditCell: (params: GridRenderEditCellParams) => <EditSelectCell params={params} options={statusOptions} />,
    },
    {
      field: 'quality',
      headerName: 'Quality',
      width: 150,
      resizable: true,
      editable: true,
      valueGetter: (_, row) => row?.quality || 'Pending Review',
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return <QualityCell row={params.row} onClick={handleClick} densityStyles={densityStyles} />
      },
      renderEditCell: (params: GridRenderEditCellParams) => <EditSelectCell params={params} options={qualityOptions} />,
    },
    {
      field: 'leadScore',
      headerName: 'Lead Score',
      width: 110,
      resizable: true,
      editable: true,
      valueGetter: (_, row) => row?.leadScore ?? null,
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return <LeadScoreCell row={params.row} onClick={handleClick} densityStyles={densityStyles} />
      },
      renderEditCell: (params: GridRenderEditCellParams) => <EditLeadScoreCell params={params} />,
    },
    {
      field: 'assignedUser',
      headerName: 'Assigned To',
      width: 100,
      resizable: true,
      editable: true,
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return <AssignedUserCell row={params.row} densityStyles={densityStyles} users={users} onClick={handleClick} />
      },
      renderEditCell: (params: GridRenderEditCellParams) => <EditUserSelectCell params={params} users={users} />,
    },
    {
      field: 'createdTime',
      headerName: 'Time Created',
      width: 150,
      resizable: true,
      valueGetter: (_, row) => {
        return row?.createdTime ? formatDateTime(row?.createdTime) : null
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'submittedDate',
      headerName: 'Submitted Date',
      width: 150,
      resizable: true,
      valueGetter: params => {
        if (!params) return null
        // Convert date string to a dayjs object
        const date = dayjs(params, 'MMMM D, YYYY')
        return date.isValid() ? date.toDate() : null
      },
      renderCell: params => {
        return <Box>{params.row ? params.row.submittedDate : ''}</Box>
      },
      sortComparator: (date1, date2) => {
        // Push null/empty dates to the bottom regardless of sort direction
        if (date1 === null && date2 === null) return 0
        if (date1 === null) return 1
        if (date2 === null) return -1
        return date1.getTime() - date2.getTime()
      },
    },
    {
      field: 'pageURL',
      headerName: 'Page Url',
      minWidth: 200,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 150,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'medium',
      headerName: 'Medium',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'keyTerm',
      headerName: 'Key Term',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'ggLid',
      headerName: 'Gglid',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'userId',
      headerName: 'User Id',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
    {
      field: 'sessionId',
      headerName: 'Session Id',
      width: 100,
      resizable: true,
      valueGetter: params => params,
    },
  ]
}
